import isEmpty from 'lodash/isEmpty';
import { arrayOf, bool, func, number, string } from 'prop-types';
import React from 'react';
import { Col, Input, Label, Row } from 'reactstrap';

import CategorySelect from '../../generic/CategorySelect';
import DateRange from '../../generic/DateRange';
import MonthRange from '../../generic/MonthRange';
import MultiSelect from '../../generic/MultiSelect';
import { timeGranularities } from '../../products/constants';
import { gendersType, timeGranularityType } from '../../prop-types';
import { countryOptions } from '../../quotes/constants';
import QuoteLineGenderDetails from './QuoteLineGenderDetails';

// Assumes both DateRange & MonthRange accept the same props
function dateRangeInput(granularity, props) {
  if (granularity === timeGranularities.MONTH) return <MonthRange {...props} />;
  return <DateRange {...props} />;
}

function Details({
  id,

  startDate,
  endDate,
  categories,

  timeGranularity,

  secondaryCountryCodes,

  isPartOfCommercialPackage,

  withTimeRange,
  withCategories,
  withGender,

  withDescription,
  serviceDesc,

  onChange,
  colDistribution,
  withPortal,
  isSalesforceQuote,
}) {
  const [labelSm, valueSm] = colDistribution;
  const namePrefix = `quoteLines[${id}]`;
  const inputProps = { onChange, 'data-quote-line-id': id };

  return (
    <Row className="mb-n3">
      <>
        <Label
          sm={labelSm}
          for={`${namePrefix}.isPartOfCommercialPackage`}
          className="mb-3"
        >
          Part of Commercial Package
        </Label>
        <Col sm={valueSm} className="mb-3">
          <Input
            id={`${namePrefix}.isPartOfCommercialPackage`}
            name="isPartOfCommercialPackage"
            checked={isPartOfCommercialPackage}
            type="checkbox"
            className="form-control form-checkbox"
            {...inputProps}
          />
        </Col>
      </>
      {(withTimeRange || isSalesforceQuote) && (
        <>
          <Label
            sm={labelSm}
            for={`${namePrefix}.dates-startDate`}
            className="mb-3"
          >
            Service Dates
          </Label>
          <Col sm={valueSm} className="mb-3">
            {dateRangeInput(timeGranularity, {
              id: `${namePrefix}.dates`,
              startName: 'startDate',
              endName: 'endDate',
              startDate,
              endDate,
              ...inputProps,
              openDirection: 'up',
              small: true,
              withPortal,
            })}
          </Col>
        </>
      )}
      {(withGender || isSalesforceQuote) && (
        <QuoteLineGenderDetails
          quoteLineId={id}
          colDistribution={colDistribution}
        />
      )}
      {!isEmpty(secondaryCountryCodes) && (
        <>
          <Label
            sm={labelSm}
            for={`${namePrefix}.secondaryCountryCodes`}
            className="mb-3"
          >
            Countries
          </Label>
          <Col sm={valueSm} className="mb-3">
            <MultiSelect
              options={countryOptions}
              id={`${namePrefix}.secondaryCountryCodes`}
              name="secondaryCountryCodes"
              value={secondaryCountryCodes}
              {...inputProps}
              readOnly
            />
          </Col>
        </>
      )}
      {(withCategories || isSalesforceQuote) && (
        <>
          <Label sm={labelSm} for={`${namePrefix}.categories`} className="mb-3">
            Categories
          </Label>
          <Col sm={valueSm} className="mb-3">
            <CategorySelect
              id={`${namePrefix}.categories`}
              name="categories"
              value={categories}
              {...inputProps}
            />
          </Col>
        </>
      )}
      {withDescription && (
        <>
          <Label
            sm={labelSm}
            for={`${namePrefix}.serviceDesc`}
            className="mb-3"
          >
            Service Description
          </Label>
          <Col sm={valueSm} className="mb-3">
            <Input
              type="textarea"
              value={serviceDesc}
              id={`${namePrefix}.serviceDesc`}
              name="serviceDesc"
              className="form-control"
              {...inputProps}
            />
          </Col>
        </>
      )}
    </Row>
  );
}

Details.propTypes = {
  id: number.isRequired,

  startDate: string,
  endDate: string,
  genders: gendersType,
  categories: arrayOf(string.isRequired),
  serviceDesc: string,

  secondaryCountryCodes: arrayOf(string.isRequired),
  isPartOfCommercialPackage: bool,

  withTimeRange: bool,
  withCategories: bool,
  withGender: bool,
  withDescription: bool,

  timeGranularity: timeGranularityType,

  onChange: func.isRequired,

  colDistribution: arrayOf(Col.propTypes.sm.isRequired),
  withPortal: bool,
  isSalesforceQuote: bool.isRequired,
};

Details.defaultProps = {
  startDate: null,
  endDate: null,
  genders: [],
  categories: [],
  serviceDesc: '',

  timeGranularity: timeGranularities.DAY,

  secondaryCountryCodes: [],
  isPartOfCommercialPackage: true,
  withTimeRange: false,
  withCategories: false,
  withGender: false,
  withDescription: false,

  colDistribution: [5, 7],
  withPortal: false,
};

export default Details;
